import jaJP from "antd/es/locale/ja_JP";
import enUS from "antd/es/locale/en_US";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import "./App.css";
import { db, auth } from "./FirebaseConfig";
import {
  collection,
  getDocs,
  getDoc,
  doc as firestoreDoc,
  query as firestoreQuery,
  orderBy,
  limit as firestoreLimit,
  startAfter,
} from "firebase/firestore";
import { onAuthStateChanged, getAuth } from "firebase/auth";
import { Table, Button, theme, Select, Input, message, DatePicker, Space, ConfigProvider } from "antd";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Navigate } from "react-router-dom";
import StomataLoader from "./StomataLoader";
import { useTranslation } from "react-i18next";
import JSZip from "jszip";
import moment from "moment";

const { RangePicker } = DatePicker;

/**
 * 既存トークン
 * 現行コードに合わせて維持
 */
const STORAGE_TOKEN = "bb06e309-ca5b-4b6b-9bdd-7ea80efa4ced";

/**
 * 初期表示・追加読み込み単位
 * 既存ユーザー向けに「全件読み込み」も残す
 */
const LOAD_STEP = 100;
const TABLE_PAGE_SIZE = 100;
const DOC_FETCH_CONCURRENCY = 8;

/**
 * 既存の画像ZIP内CSV用
 * 既存仕様を壊さないため残す
 */
const ZIP_CSV_FIELDS = ["aperture", "axisLong", "axisShort", "area"];

/**
 * 既存CSVヘッダ名の置換
 * 既存仕様を壊さないため残す
 */
const CSV_HEADER_MAP = {
  photopath: "ImageName",
  cultiverName: "Variety",
  date: "Date",
  aperture: "Aperture",
  size: "Count",
  location: "Location",
  important: "Importance",
  freeText: "Notes",
};

/**
 * 新規CSV: 画像単位サマリー
 */
const SUMMARY_HEADERS = [
  "ImageName",
  "UserName",
  "Variety",
  "Date",
  "Timestamp",
  "Location",
  "Importance",
  "Notes",
  "Weather",
  "Temperature",
  "Humidity",
  "Count",
  "AvgAperture",
  "AvgArea",
  "AvgAxisLong",
  "AvgAxisShort",
  "OpenCount",
  "ClosedCount",
  "HasResults",
];

/**
 * 新規CSV: 気孔単位詳細
 */
const STOMATA_HEADERS = [
  "ImageName",
  "UserName",
  "Variety",
  "Date",
  "Timestamp",
  "StomaIndex",
  "StomaId",
  "Label",
  "Aperture",
  "Area",
  "AxisLong",
  "AxisShort",
  "Confidence",
  "Left",
  "Top",
  "Right",
  "Bottom",
  "CenterX",
  "CenterY",
  "Temperature",
  "Humidity",
  "Location",
  "Importance",
  "Notes",
];

/**
 * 新規CSV: 環境データ
 */
const ENVIRONMENT_HEADERS = [
  "ImageName",
  "UserName",
  "Variety",
  "Date",
  "Timestamp",
  "Weather",
  "Temperature",
  "Humidity",
  "Location",
  "Importance",
  "Notes",
];

/**
 * 新規CSV: スキップ画像ログ
 */
const SKIPPED_HEADERS = [
  "ImageName",
  "Reason",
  "HasResults",
  "UserName",
  "Variety",
  "Date",
];

function getCurrentDateTime() {
  const now = new Date();
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, "0");
  const day = now.getDate().toString().padStart(2, "0");
  const hours = now.getHours().toString().padStart(2, "0");
  const minutes = now.getMinutes().toString().padStart(2, "0");
  const seconds = now.getSeconds().toString().padStart(2, "0");
  return `${year}${month}${day}_${hours}${minutes}${seconds}`;
}

function safeFilename(name) {
  return String(name || "file")
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, "_");
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Firestore側の timestamp が
 * - Unix秒
 * - Unix秒 * 10
 * - Unixミリ秒
 * など揺れていても、2000〜2100年に入る候補を採用する
 */
function normalizeNumericTimestamp(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;

  const candidates = [n, n * 10, n * 100, n * 1000];

  for (const ms of candidates) {
    const year = new Date(ms).getFullYear();
    if (year >= 2000 && year <= 2100) return Math.round(ms);
  }

  return n;
}

function toMs(ts) {
  if (!ts) return null;

  if (typeof ts.toDate === "function") return ts.toDate().getTime();

  if (typeof ts === "number") return normalizeNumericTimestamp(ts);

  if (ts instanceof Date) return ts.getTime();

  const s = String(ts).trim();
  const m = moment(
    s,
    ["YY/MM/DD HH:mm:ss", "YYYY/MM/DD HH:mm:ss", "YYYY-MM-DD HH:mm:ss", moment.ISO_8601],
    true
  );

  if (m.isValid()) return m.valueOf();

  const m2 = moment(s);
  return m2.isValid() ? m2.valueOf() : null;
}

function displayTimestamp(ts) {
  if (!ts) return "";

  if (typeof ts.toDate === "function") {
    return ts.toDate().toISOString();
  }

  if (ts instanceof Date) {
    return ts.toISOString();
  }

  if (typeof ts === "object") {
    try {
      return JSON.stringify(ts);
    } catch (_) {
      return String(ts);
    }
  }

  return ts;
}

function formatNumber(value, digits = 2) {
  const n = toFiniteNumber(value);
  return n === null ? "" : n.toFixed(digits);
}

function averageField(results, field, digits = 2) {
  const nums = (results || [])
    .map((item) => toFiniteNumber(item?.[field]))
    .filter((v) => v !== null);

  if (!nums.length) return "";

  const sum = nums.reduce((acc, v) => acc + v, 0);
  return (sum / nums.length).toFixed(digits);
}

function getResultLabel(item) {
  return item?.title ?? item?.label ?? item?.class ?? "";
}

function countByLabel(results, label) {
  const target = String(label).toLowerCase();

  return (results || []).filter((item) => {
    const current = String(getResultLabel(item)).toLowerCase();
    return current === target;
  }).length;
}

function escCsv(value) {
  if (value === null || value === undefined) return "";

  const s = String(value);

  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }

  return s;
}

function csvRowsToString(rows) {
  return rows.map((row) => row.map(escCsv).join(",")).join("\n") + "\n";
}

function withBom(csv) {
  return "\uFEFF" + csv;
}

function normalizeResults(results) {
  if (!Array.isArray(results)) return [];

  return results.map((item, idx) => {
    const assignedId =
      item?.id !== undefined && item?.id !== "" ? item.id : idx.toString();

    return {
      ...item,
      id: assignedId,
    };
  });
}

function normalizePost(key, data = {}) {
  const normalizedAperture = toFiniteNumber(data.aperture);
  const normalizedSize = toFiniteNumber(data.size);

  return {
    key,
    photopath: data.photopath ?? "",
    cultiverName: data.cultiverName ?? "",
    date: data.date ?? "",
    aperture: normalizedAperture,
    size: normalizedSize ?? data.size ?? "",
    location: data.location ?? "",
    important: data.important ?? "",
    freeText: data.freeText ?? "",
    userName: data.userName ?? "",
    timestamp: data.timestamp ?? "",
    timestampMs: toMs(data.timestamp) ?? toMs(data.date),

    /**
     * 既存データに誤字フィールドがある可能性も吸収
     * screenshot上では temperture / wether の可能性があるため両対応
     */
    temperature: data.temperature ?? data.temperture ?? "",
    humidity: data.humidity ?? "",
    weather: data.weather ?? data.wether ?? "",
    updatedAt: data.updatedAt ?? null,
  };
}

function normalizePostWithOwner(key, data = {}, ownerEmail = "") {
  const normalized = normalizePost(key, data);
  const safeOwnerEmail = normalizeEmail(ownerEmail);

  return {
    ...normalized,
    userName: normalized.userName || safeOwnerEmail || "",
    ownerEmail: safeOwnerEmail,
  };
}

function mergePosts(prev, next) {
  const map = new Map();

  for (const item of prev) {
    map.set(item.key, item);
  }

  for (const item of next) {
    map.set(item.key, item);
  }

  return Array.from(map.values());
}

function sortPostsDesc(posts) {
  return [...posts].sort((a, b) => {
    const ta = a.timestampMs ?? 0;
    const tb = b.timestampMs ?? 0;

    if (ta !== tb) return tb - ta;

    return String(b.date || "").localeCompare(String(a.date || ""));
  });
}

function getRangeBoundaryMs(value, mode) {
  if (!value) return null;

  const v = typeof value.clone === "function" ? value.clone() : value;

  if (mode === "start") return v.startOf("day").valueOf();
  return v.endOf("day").valueOf();
}

async function asyncPool(items, concurrency, worker) {
  const results = new Array(items.length);
  let currentIndex = 0;

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    async () => {
      while (currentIndex < items.length) {
        const index = currentIndex;
        currentIndex += 1;
        results[index] = await worker(items[index], index);
      }
    }
  );

  await Promise.all(workers);
  return results;
}

function uniqueZipPath(usedPaths, path) {
  if (!usedPaths.has(path)) {
    usedPaths.add(path);
    return path;
  }

  const slashIndex = path.lastIndexOf("/");
  const dir = slashIndex >= 0 ? path.slice(0, slashIndex + 1) : "";
  const filename = slashIndex >= 0 ? path.slice(slashIndex + 1) : path;

  const dotIndex = filename.lastIndexOf(".");
  const base = dotIndex >= 0 ? filename.slice(0, dotIndex) : filename;
  const ext = dotIndex >= 0 ? filename.slice(dotIndex) : "";

  let count = 2;
  let candidate = `${dir}${base}_${count}${ext}`;

  while (usedPaths.has(candidate)) {
    count += 1;
    candidate = `${dir}${base}_${count}${ext}`;
  }

  usedPaths.add(candidate);
  return candidate;
}

function makeSkippedRow(doc, reason) {
  return {
    imageName: doc.photopath || doc.key || "",
    reason,
    hasResults: Boolean(doc.results?.length),
    userName: doc.userName || "",
    variety: doc.cultiverName || "",
    date: doc.date || "",
  };
}

function buildSkippedImagesCsvContent(skippedRows) {
  const rows = [SKIPPED_HEADERS];

  for (const item of skippedRows || []) {
    rows.push([
      item.imageName,
      item.reason,
      item.hasResults ? "true" : "false",
      item.userName,
      item.variety,
      item.date,
    ]);
  }

  return csvRowsToString(rows);
}

function buildSummaryCsvContent(docs) {
  const rows = [SUMMARY_HEADERS];

  for (const doc of docs) {
    const results = doc.results || [];

    rows.push([
      doc.photopath,
      doc.userName,
      doc.cultiverName,
      doc.date,
      displayTimestamp(doc.timestamp),
      doc.location,
      doc.important,
      doc.freeText,
      doc.weather,
      doc.temperature,
      doc.humidity,
      doc.size ?? results.length,
      averageField(results, "aperture"),
      averageField(results, "area"),
      averageField(results, "axisLong"),
      averageField(results, "axisShort"),
      countByLabel(results, "open"),
      countByLabel(results, "closed"),
      results.length ? "true" : "false",
    ]);
  }

  return csvRowsToString(rows);
}

function buildStomataCsvContent(docs) {
  const rows = [STOMATA_HEADERS];

  for (const doc of docs) {
    const results = doc.results || [];

    results.forEach((item, index) => {
      const loc = item.location || {};

      const left = toFiniteNumber(loc.left);
      const top = toFiniteNumber(loc.top);
      const right = toFiniteNumber(loc.right);
      const bottom = toFiniteNumber(loc.bottom);

      const centerX =
        left !== null && right !== null ? ((left + right) / 2).toFixed(2) : "";

      const centerY =
        top !== null && bottom !== null ? ((top + bottom) / 2).toFixed(2) : "";

      rows.push([
        doc.photopath,
        doc.userName,
        doc.cultiverName,
        doc.date,
        displayTimestamp(doc.timestamp),
        index,
        item.id ?? "",
        getResultLabel(item),
        formatNumber(item.aperture),
        formatNumber(item.area),
        formatNumber(item.axisLong),
        formatNumber(item.axisShort),
        formatNumber(item.confidence),
        left ?? "",
        top ?? "",
        right ?? "",
        bottom ?? "",
        centerX,
        centerY,
        doc.temperature,
        doc.humidity,
        doc.location,
        doc.important,
        doc.freeText,
      ]);
    });
  }

  return csvRowsToString(rows);
}

function buildEnvironmentCsvContent(docs) {
  const rows = [ENVIRONMENT_HEADERS];

  for (const doc of docs) {
    rows.push([
      doc.photopath,
      doc.userName,
      doc.cultiverName,
      doc.date,
      displayTimestamp(doc.timestamp),
      doc.weather,
      doc.temperature,
      doc.humidity,
      doc.location,
      doc.important,
      doc.freeText,
    ]);
  }

  return csvRowsToString(rows);
}

function buildLegacyCsvContent(docs, fields, staticHeaders, headerLabels) {
  const lines = [];

  let maxColumns = 0;

  for (const d of docs) {
    const len = d.results?.length || 0;
    if (len > maxColumns) maxColumns = len;
  }

  const dynamicHeaders = fields.flatMap((field) =>
    Array.from({ length: maxColumns }, (_, i) => `${field}_${i}`)
  );

  lines.push([...headerLabels, ...dynamicHeaders]);

  for (const doc of docs) {
    const row = staticHeaders.map((k) => {
      const v = doc[k];

      if (v?.toDate) {
        return v.toDate().toLocaleString("ja-JP", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      }

      return typeof v === "number" ? v : v ?? "";
    });

    const results = doc.results || [];

    const resultValues = fields.flatMap((field) =>
      Array.from({ length: maxColumns }, (_, i) => {
        const v = results?.[i]?.[field];
        return typeof v === "number" ? v.toFixed(2) : v ?? "";
      })
    );

    lines.push([...row, ...resultValues]);
  }

  return csvRowsToString(lines);
}

/**
 * 展開行
 */
const ExpandedResults = React.memo(function ExpandedResults({
  results,
  fields,
  loading,
}) {
  const { t } = useTranslation();
  if (loading) {
    return <div style={{ padding: 8, opacity: 0.7 }}>{t("download.ui.loadingResults")}</div>;
  }

  if (!results?.length) {
    return <div style={{ padding: 8, opacity: 0.7 }}>{t("download.ui.noResults")}</div>;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {results.map((_, i) => (
              <th key={i} style={{ textAlign: "center", padding: "4px 8px" }}>
                {`${fields?.[0] ?? "field"}_${i}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fields.map((field) => (
            <tr key={field}>
              {results.map((item, i) => (
                <td key={i} style={{ textAlign: "center", padding: "4px 8px" }}>
                  {typeof item[field] === "number"
                    ? item[field].toFixed(2)
                    : item[field] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

function Download() {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const t = useCallback((key, options) => i18n.t(key, options), [i18n]);

  const [authReady, setAuthReady] = useState(false);
  const [posts, setPosts] = useState([]);
  const [resultsByKey, setResultsByKey] = useState({});
  const [expandedLoadingKeys, setExpandedLoadingKeys] = useState({});

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);

  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadedAll, setLoadedAll] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedType, setSelectedType] = useState("aperture");
  const [user, setUser] = useState(null);
  const [selectedResultFields, setSelectedResultFields] = useState(["aperture"]);

  const [isAdmin, setIsAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  const [targetEmailInput, setTargetEmailInput] = useState("");
  const [activeTargetEmail, setActiveTargetEmail] = useState("");

  const [dateRange, setDateRange] = useState(null);
  const [isDateFiltered, setIsDateFiltered] = useState(false);

  const resultsCacheRef = useRef({});
  const fullDocCacheRef = useRef({});

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const tableLocale = useMemo(
    () => ({
      triggerAsc: t("table.triggerAsc"),
      triggerDesc: t("table.triggerDesc"),
      cancelSort: t("table.cancelSort"),
      filterConfirm: t("table.filterConfirm"),
      filterReset: t("table.filterReset"),
      emptyText: t("common.emptyText") || "-",
    }),
    [t, language]
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (!currentUser) {
        setIsAdmin(false);
        setAdminChecked(true);
        setAuthReady(true);
        setTargetEmailInput("");
        setActiveTargetEmail("");
        return;
      }

      const ownEmail = normalizeEmail(currentUser.email);
      setTargetEmailInput(ownEmail);
      setActiveTargetEmail(ownEmail);

      try {
        const userDocRef = firestoreDoc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userDocRef);
        setIsAdmin(userSnap.exists() && userSnap.data()?.role === "admin");
      } catch (e) {
        console.warn("Admin role check failed:", e);
        setIsAdmin(false);
      } finally {
        setAdminChecked(true);
        setAuthReady(true);
      }
    });

    return () => unsubscribe();
  }, []);

  const resetLocalCaches = useCallback(() => {
    resultsCacheRef.current = {};
    fullDocCacheRef.current = {};
    setResultsByKey({});
    setExpandedLoadingKeys({});
  }, []);

  const getTargetEmail = useCallback(
    (overrideEmail = null) => {
      const fallbackEmail = user?.email || getAuth().currentUser?.email || "";
      return normalizeEmail(overrideEmail ?? activeTargetEmail ?? fallbackEmail);
    },
    [activeTargetEmail, user?.email]
  );

  const fetchFirstPage = useCallback(async (overrideEmail = null) => {
    const email = getTargetEmail(overrideEmail);

    if (!email) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      setPosts([]);
      setSelectedRowKeys([]);
      setLastVisible(null);
      setHasMore(false);
      setLoadedAll(false);
      resetLocalCaches();

      const colRef = collection(db, email);

      /**
       * 高速表示:
       * 初期は timestamp desc で直近100件のみ
       *
       * 既存データで timestamp がないものはこのクエリに出ない可能性あり。
       * その場合は画面上の「全件読み込み」で従来互換の全件取得ができる。
       */
      const q = firestoreQuery(
        colRef,
        orderBy("timestamp", "desc"),
        firestoreLimit(LOAD_STEP + 1)
      );

      const snapshot = await getDocs(q);

      const rawDocs = snapshot.docs.filter((d) => d.id !== "_settings");
      const pageDocs = rawDocs.slice(0, LOAD_STEP);

      const nextPosts = pageDocs.map((d) => normalizePostWithOwner(d.id, d.data(), email));

      setPosts(nextPosts);
      setLastVisible(pageDocs[pageDocs.length - 1] || null);
      setHasMore(rawDocs.length > LOAD_STEP);
      setLoadedAll(false);
    } catch (e) {
      console.warn("Fast loading failed. Fallback to full compatible loading.", e);

      /**
       * 互換フォールバック:
       * orderByが失敗した場合のみ従来に近い全件読み込みに戻す
       */
      try {
        const snapshot = await getDocs(collection(db, email));

        const allPosts = snapshot.docs
          .filter((d) => d.id !== "_settings")
          .map((d) => normalizePostWithOwner(d.id, d.data(), email));

        setPosts(sortPostsDesc(allPosts));
        setLastVisible(null);
        setHasMore(false);
        setLoadedAll(true);

        message.warning({
          content:
            t("download.ui.fallback"),
          duration: 4,
        });
      } catch (fallbackError) {
        console.error(fallbackError);
        message.error(t("download.ui.fetchFailed"));
      }
    } finally {
      setLoading(false);
    }
  }, [getTargetEmail, resetLocalCaches, t]);

  useEffect(() => {
    if (!authReady || !adminChecked) return;

    if (!user) {
      setLoading(false);
      return;
    }

    if (!activeTargetEmail) return;

    fetchFirstPage(activeTargetEmail);
  }, [authReady, adminChecked, user, activeTargetEmail, fetchFirstPage]);

  const loadMorePosts = useCallback(async () => {
    const email = getTargetEmail();

    if (!email || !lastVisible || !hasMore || loadingMore) return;

    setLoadingMore(true);

    try {
      const colRef = collection(db, email);

      const q = firestoreQuery(
        colRef,
        orderBy("timestamp", "desc"),
        startAfter(lastVisible),
        firestoreLimit(LOAD_STEP + 1)
      );

      const snapshot = await getDocs(q);

      const rawDocs = snapshot.docs.filter((d) => d.id !== "_settings");
      const pageDocs = rawDocs.slice(0, LOAD_STEP);

      const nextPosts = pageDocs.map((d) => normalizePostWithOwner(d.id, d.data(), email));

      setPosts((prev) => mergePosts(prev, nextPosts));
      setLastVisible(pageDocs[pageDocs.length - 1] || lastVisible);
      setHasMore(rawDocs.length > LOAD_STEP);
    } catch (e) {
      console.error(e);
      message.error(t("download.ui.moreFailed"));
    } finally {
      setLoadingMore(false);
    }
  }, [getTargetEmail, hasMore, lastVisible, loadingMore, t]);

  const loadAllRows = useCallback(async () => {
    const email = getTargetEmail();

    if (!email || loadingAll) return;

    setLoadingAll(true);

    try {
      const snapshot = await getDocs(collection(db, email));

      const allPosts = snapshot.docs
        .filter((d) => d.id !== "_settings")
        .map((d) => normalizePostWithOwner(d.id, d.data(), email));

      setPosts(sortPostsDesc(allPosts));
      setLastVisible(null);
      setHasMore(false);
      setLoadedAll(true);

      message.success(t("download.ui.allLoaded", { count: allPosts.length }));
    } catch (e) {
      console.error(e);
      message.error(t("download.ui.allFailed"));
    } finally {
      setLoadingAll(false);
    }
  }, [getTargetEmail, loadingAll, t]);

  const loadFullDoc = useCallback(async (key) => {
    if (!key) return { key, results: [] };

    if (fullDocCacheRef.current[key]) {
      return fullDocCacheRef.current[key];
    }

    const email = getTargetEmail();

    if (!email) return { key, results: [] };

    const snap = await getDoc(firestoreDoc(db, email, key));

    if (!snap.exists()) {
      const fallback = { key, results: [] };
      fullDocCacheRef.current[key] = fallback;
      resultsCacheRef.current[key] = [];
      setResultsByKey((prev) => ({ ...prev, [key]: [] }));
      return fallback;
    }

    const data = snap.data();
    const normalized = normalizePostWithOwner(snap.id, data, email);
    const results = normalizeResults(data.results);

    const fullDoc = {
      ...normalized,
      results,
    };

    fullDocCacheRef.current[key] = fullDoc;
    resultsCacheRef.current[key] = results;

    setResultsByKey((prev) => {
      if (prev[key]) return prev;
      return { ...prev, [key]: results };
    });

    return fullDoc;
  }, [getTargetEmail]);

  const hydrateDocs = useCallback(
    async (
      docs,
      {
        includeResults = true,
        progressLabel = t("download.ui.fetching"),
        progressKey = "hydrate-progress",
      } = {}
    ) => {
      if (!docs.length) return [];

      if (!includeResults) {
        return docs.map((d) => ({
          ...d,
          results: resultsCacheRef.current[d.key] || [],
        }));
      }

      let done = 0;

      message.loading({
        content: `${progressLabel}… 0/${docs.length}`,
        key: progressKey,
        duration: 0,
      });

      const hydrated = await asyncPool(
        docs,
        DOC_FETCH_CONCURRENCY,
        async (item) => {
          const full = await loadFullDoc(item.key);

          done += 1;

          if (done === 1 || done % 20 === 0 || done === docs.length) {
            message.loading({
              content: `${progressLabel}… ${done}/${docs.length}`,
              key: progressKey,
              duration: 0,
            });
          }

          return {
            ...item,
            ...full,
            results: full?.results || [],
          };
        }
      );

      return hydrated;
    },
    [loadFullDoc, t]
  );

  useEffect(() => {
    if (["aperture", "area", "axisLong", "axisShort", "confidence"].includes(selectedType)) {
      setSelectedResultFields([selectedType]);
    }
  }, [selectedType]);

  const handleSelectChange = useCallback((keys) => {
    setSelectedRowKeys(keys);
  }, []);

  const selectedKeySet = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys]);

  const filteredPosts = useMemo(() => {
    if (!isDateFiltered || !dateRange) return posts;

    const [start, end] = dateRange;

    const startMs = getRangeBoundaryMs(start, "start");
    const endMs = getRangeBoundaryMs(end, "end");

    if (startMs === null || endMs === null) return posts;

    return posts.filter((p) => {
      if (!p.timestampMs) return false;
      return p.timestampMs >= startMs && p.timestampMs <= endMs;
    });
  }, [posts, isDateFiltered, dateRange]);

  const selectedData = useMemo(
    () => filteredPosts.filter((doc) => selectedKeySet.has(doc.key)),
    [filteredPosts, selectedKeySet]
  );

  const handleDateChange = useCallback((dates) => {
    if (!dates) {
      setDateRange(null);
      setIsDateFiltered(false);
      return;
    }

    setDateRange(dates);
    setIsDateFiltered(true);
  }, []);

  const handleResetDate = useCallback(() => {
    setDateRange(null);
    setIsDateFiltered(false);
  }, []);

  const handleTargetEmailSearch = useCallback(
    (value = "") => {
      const nextEmail = normalizeEmail(value || targetEmailInput || user?.email || "");

      if (!nextEmail) {
        message.warning(t("download.ui.emailRequired"));
        return;
      }

      if (nextEmail.includes("/")) {
        message.error(t("download.ui.emailSlash"));
        return;
      }

      if (!isAdmin && nextEmail !== normalizeEmail(user?.email)) {
        message.error(t("download.ui.adminOnly"));
        return;
      }

      setTargetEmailInput(nextEmail);

      if (nextEmail === activeTargetEmail) {
        fetchFirstPage(nextEmail);
      } else {
        setActiveTargetEmail(nextEmail);
      }
    },
    [activeTargetEmail, fetchFirstPage, isAdmin, targetEmailInput, user?.email, t]
  );

  const handleLoadOwnData = useCallback(() => {
    const ownEmail = normalizeEmail(user?.email || "");

    if (!ownEmail) return;

    setTargetEmailInput(ownEmail);

    if (ownEmail === activeTargetEmail) {
      fetchFirstPage(ownEmail);
    } else {
      setActiveTargetEmail(ownEmail);
    }
  }, [activeTargetEmail, fetchFirstPage, user?.email]);

  const columns = useMemo(() => getColumns(t, language), [t, language]);

  const staticHeaders = useMemo(
    () => columns.map((c) => c.dataIndex).filter(Boolean),
    [columns]
  );

  const headerLabels = useMemo(
    () => staticHeaders.map((k) => CSV_HEADER_MAP[k] ?? k),
    [staticHeaders]
  );

  const handleExpand = useCallback(
    async (expanded, record) => {
      if (!expanded || !record?.key) return;

      if (resultsCacheRef.current[record.key]) return;

      setExpandedLoadingKeys((prev) => ({
        ...prev,
        [record.key]: true,
      }));

      try {
        await loadFullDoc(record.key);
      } catch (e) {
        console.error(e);
        message.error(t("download.ui.resultsFailed"));
      } finally {
        setExpandedLoadingKeys((prev) => {
          const next = { ...prev };
          delete next[record.key];
          return next;
        });
      }
    },
    [loadFullDoc, t]
  );

  const downloadCsvFile = useCallback((csvContent, tag) => {
    const filename = `StomataStorage_${tag}_${getCurrentDateTime()}.csv`;
    const blob = new Blob([withBom(csvContent)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }, []);

  const saveZip = useCallback(async (zip, filename, progressKey = "zip-progress") => {
    message.loading({
      content: t("download.ui.creatingZip"),
      key: progressKey,
      duration: 0,
    });

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const zipUrl = URL.createObjectURL(zipBlob);

    const a = document.createElement("a");
    a.href = zipUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(zipUrl), 10_000);

    message.success({
      content: t("download.ui.zipDownloaded"),
      key: progressKey,
      duration: 2,
    });
  }, [t]);

  const buildStorageUrl = useCallback((doc, userNameOverride, withToken) => {
    const userName = userNameOverride || doc.userName || getAuth().currentUser?.email || "";
    const cultivar = doc.cultiverName || "";
    const photo = doc.photopath || "";

    if (!userName || !cultivar || !photo) return null;

    const base =
      `https://firebasestorage.googleapis.com/v0/b/stomatascope-fdf90.appspot.com/o/` +
      `${encodeURIComponent(userName)}%2F${encodeURIComponent(cultivar)}%2F${encodeURIComponent(photo)}` +
      `?alt=media`;

    return withToken ? `${base}&token=${STORAGE_TOKEN}` : base;
  }, []);

  const fetchImageBlob = useCallback(
    async (doc) => {
      const currentEmail = normalizeEmail(getAuth().currentUser?.email || "");
      const ownerEmail = normalizeEmail(doc.ownerEmail || getTargetEmail());
      const candidates = Array.from(
        new Set([doc.userName, ownerEmail, currentEmail].filter(Boolean))
      );

      if (!doc.photopath || !doc.cultiverName || !candidates.length) {
        return null;
      }

      const tryUrls = [];

      for (const u of candidates) {
        const urlWithToken = buildStorageUrl(doc, u, true);
        const urlWithoutToken = buildStorageUrl(doc, u, false);

        if (urlWithToken) tryUrls.push(urlWithToken);
        if (urlWithoutToken) tryUrls.push(urlWithoutToken);
      }

      for (const url of tryUrls) {
        try {
          const res = await fetch(url);

          if (res.ok) {
            return await res.blob();
          }
        } catch (_) {
          // 次の候補URLを試す
        }
      }

      return null;
    },
    [buildStorageUrl, getTargetEmail]
  );

  const decodeToDrawable = useCallback(async (blob) => {
    if (typeof createImageBitmap === "function") {
      try {
        const bmp = await createImageBitmap(blob);
        return {
          kind: "bitmap",
          img: bmp,
          width: bmp.width,
          height: bmp.height,
        };
      } catch (_) {
        // fallback
      }
    }

    const url = URL.createObjectURL(blob);

    try {
      const img = new Image();
      img.decoding = "async";
      img.src = url;
      await img.decode();

      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;

      return {
        kind: "image",
        img,
        width: w,
        height: h,
        revoke: () => URL.revokeObjectURL(url),
      };
    } catch (e) {
      URL.revokeObjectURL(url);
      throw e;
    }
  }, []);

  const makeAnnotatedBlob = useCallback(
    async (originalBlob, results) => {
      const decoded = await decodeToDrawable(originalBlob);

      const canvas = document.createElement("canvas");
      canvas.width = decoded.width;
      canvas.height = decoded.height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(decoded.img, 0, 0, decoded.width, decoded.height);

      if (decoded.kind === "bitmap" && typeof decoded.img.close === "function") {
        decoded.img.close();
      }

      if (decoded.kind === "image" && decoded.revoke) {
        decoded.revoke();
      }

      for (const [index, item] of (results || []).entries()) {
        const loc = item.location || {};

        const left = toFiniteNumber(loc.left);
        const top = toFiniteNumber(loc.top);
        const right = toFiniteNumber(loc.right);
        const bottom = toFiniteNumber(loc.bottom);

        if ([left, top, right, bottom].some((v) => v === null)) continue;

        const color = getResultLabel(item) === "open" ? "#ff0000" : "#0000ff";

        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.strokeRect(left, top, right - left, bottom - top);

        ctx.font = "16px Arial";
        ctx.fillStyle = color;

        if (toFiniteNumber(item.aperture) !== null) {
          ctx.textBaseline = "bottom";
          ctx.textAlign = "left";
          ctx.fillText(`${Number(item.aperture).toFixed(2)}µm`, left, top - 6);
        }

        ctx.textBaseline = "bottom";
        ctx.textAlign = "right";
        ctx.fillText(`${item.id ?? index + 1}`, right - 4, bottom - 4);
      }

      return await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob failed"))),
          "image/jpeg",
          0.95
        );
      });
    },
    [decodeToDrawable]
  );

  const addImagesToZip = useCallback(
    async (
      zip,
      docs,
      {
        includeOriginal,
        includeAnnotated,
        originalDir = "images/original",
        annotatedDir = "images/annotated",
        progressKey = "zip-progress",
      }
    ) => {
      const skipped = [];
      const usedPaths = new Set();

      let originalCount = 0;
      let annotatedCount = 0;

      for (let i = 0; i < docs.length; i++) {
        const item = docs[i];
        const fname = safeFilename(item.photopath || `${item.key}.jpg`);

        if (i === 0 || (i + 1) % 10 === 0 || i + 1 === docs.length) {
          message.loading({
            content: t("download.ui.addingImages", { current: i + 1, total: docs.length }),
            key: progressKey,
            duration: 0,
          });
        }

        if (!item.photopath || !item.cultiverName) {
          skipped.push(makeSkippedRow(item, "invalid_storage_path"));
          continue;
        }

        let originalBlob = null;

        try {
          originalBlob = await fetchImageBlob(item);

          if (!originalBlob) {
            skipped.push(makeSkippedRow(item, "image_not_found"));
            continue;
          }

          if (includeOriginal) {
            const path = uniqueZipPath(usedPaths, `${originalDir}/${fname}`);
            zip.file(path, originalBlob);
            originalCount += 1;
          }

          if (includeAnnotated) {
            const results = item.results || [];

            if (!results.length) {
              skipped.push(makeSkippedRow(item, "results_empty"));
              continue;
            }

            try {
              const annotatedBlob = await makeAnnotatedBlob(originalBlob, results);
              const path = uniqueZipPath(usedPaths, `${annotatedDir}/${fname}`);
              zip.file(path, annotatedBlob);
              annotatedCount += 1;
            } catch (e) {
              console.warn("Annotated image failed:", fname, e);
              skipped.push(makeSkippedRow(item, "annotated_failed"));
            }
          }
        } catch (e) {
          console.warn("Image fetch failed:", fname, e);
          skipped.push(makeSkippedRow(item, "fetch_failed"));
        }
      }

      return {
        skipped,
        originalCount,
        annotatedCount,
      };
    },
    [fetchImageBlob, makeAnnotatedBlob, t]
  );

  const buildLegacyCsv = useCallback(
    async (docs, fields) => {
      const hydrated = await hydrateDocs(docs, {
        includeResults: true,
        progressLabel: t("download.ui.fetchingCsv"),
        progressKey: "download-progress",
      });

      return buildLegacyCsvContent(hydrated, fields, staticHeaders, headerLabels);
    },
    [hydrateDocs, staticHeaders, headerLabels, t]
  );

  const downloadSummaryCsv = useCallback(
    async (docs) => {
      const hydrated = await hydrateDocs(docs, {
        includeResults: true,
        progressLabel: t("download.ui.creatingSummary"),
        progressKey: "download-progress",
      });

      const csv = buildSummaryCsvContent(hydrated);
      downloadCsvFile(csv, "summary");

      message.success({
        content: t("download.ui.summaryDownloaded"),
        key: "download-progress",
        duration: 2,
      });
    },
    [downloadCsvFile, hydrateDocs, t]
  );

  const downloadStomataCsv = useCallback(
    async (docs) => {
      const hydrated = await hydrateDocs(docs, {
        includeResults: true,
        progressLabel: t("download.ui.creatingStomata"),
        progressKey: "download-progress",
      });

      const csv = buildStomataCsvContent(hydrated);
      downloadCsvFile(csv, "stomata");

      message.success({
        content: t("download.ui.stomataDownloaded"),
        key: "download-progress",
        duration: 2,
      });
    },
    [downloadCsvFile, hydrateDocs, t]
  );

  const downloadEnvironmentCsv = useCallback(
    async (docs) => {
      const csv = buildEnvironmentCsvContent(docs);
      downloadCsvFile(csv, "environment");

      message.success({
        content: t("download.ui.environmentDownloaded"),
        duration: 2,
      });
    },
    [downloadCsvFile, t]
  );

  /**
   * 既存画像ZIP
   * selectedType:
   * - image_original
   * - image_annotated
   *
   * 既存ユーザー向けに残す。
   */
  const downloadImagesZipLegacy = useCallback(
    async (mode) => {
      const docs = selectedData;
      if (!docs.length) return;

      const ts = getCurrentDateTime();
      const zip = new JSZip();
      const progressKey = "zip-progress";

      const hydrated = await hydrateDocs(docs, {
        includeResults: true,
        progressLabel: t("download.ui.fetchingZip"),
        progressKey,
      });

      for (const field of ZIP_CSV_FIELDS) {
        const csv = buildLegacyCsvContent(hydrated, [field], staticHeaders, headerLabels);
        zip.file(`csv/${field}_${ts}.csv`, withBom(csv));
      }

      const imageResult = await addImagesToZip(zip, hydrated, {
        includeOriginal: mode === "original",
        includeAnnotated: mode === "annotated",
        originalDir: "original",
        annotatedDir: "annotated",
        progressKey,
      });

      const imageFetchFailed = imageResult.skipped.filter(
        (s) => s.reason !== "results_empty"
      ).length;

      const readmeLines = [
        "StomataScope Image ZIP",
        `created_at: ${ts}`,
        `mode: ${mode}`,
        `selected_images: ${docs.length}`,
        "",
        "Folders:",
        ...(mode === "original"
          ? ["- original/ : original images (no re-encode)"]
          : []),
        ...(mode === "annotated"
          ? ["- annotated/: annotated images (only when results exist)"]
          : []),
        "- csv/      : CSV files per metric",
        "",
        "CSV files included:",
        ...ZIP_CSV_FIELDS.map((f) => `- ${f}_${ts}.csv`),
        "",
        ...(mode === "annotated"
          ? [`annotated_created: ${imageResult.annotatedCount}`]
          : []),
        `image_fetch_failed: ${imageFetchFailed}`,
      ];

      zip.file("README.txt", readmeLines.join("\n"));

      await saveZip(
        zip,
        mode === "annotated"
          ? `stomata_images_annotated_${ts}.zip`
          : `stomata_images_original_${ts}.zip`,
        progressKey
      );

      if (imageFetchFailed > 0) {
        message.warning({
          content: t("download.ui.imagesFailed", { count: imageFetchFailed }),
          duration: 4,
        });
      }
    },
    [
      selectedData,
      hydrateDocs,
      staticHeaders,
      headerLabels,
      addImagesToZip,
      saveZip,
    , t]
  );

  /**
   * 新規画像ZIP
   * selectedType:
   * - image_original_v2
   * - image_annotated_v2
   */
  const downloadImagesZipV2 = useCallback(
    async (mode) => {
      const docs = selectedData;
      if (!docs.length) return;

      const ts = getCurrentDateTime();
      const zip = new JSZip();
      const progressKey = "zip-progress";

      const hydrated = await hydrateDocs(docs, {
        includeResults: true,
        progressLabel: t("download.ui.fetchingNewZip"),
        progressKey,
      });

      zip.file("csv/summary.csv", withBom(buildSummaryCsvContent(hydrated)));
      zip.file("csv/environment.csv", withBom(buildEnvironmentCsvContent(hydrated)));

      if (mode === "annotated") {
        zip.file("csv/stomata.csv", withBom(buildStomataCsvContent(hydrated)));
      }

      const imageResult = await addImagesToZip(zip, hydrated, {
        includeOriginal: mode === "original",
        includeAnnotated: mode === "annotated",
        originalDir: "images/original",
        annotatedDir: "images/annotated",
        progressKey,
      });

      zip.file(
        "csv/skipped_images.csv",
        withBom(buildSkippedImagesCsvContent(imageResult.skipped))
      );

      const readmeLines = [
        "StomataScope Image Export v2",
        `created_at: ${ts}`,
        `mode: ${mode}`,
        `selected_images: ${docs.length}`,
        "",
        "Folders:",
        ...(mode === "original"
          ? ["- images/original/ : original images"]
          : []),
        ...(mode === "annotated"
          ? ["- images/annotated/: annotated images"]
          : []),
        "- csv/             : metadata CSV files",
        "",
        "CSV files included:",
        "- summary.csv",
        "- environment.csv",
        ...(mode === "annotated" ? ["- stomata.csv"] : []),
        "- skipped_images.csv",
        "",
        `original_created: ${imageResult.originalCount}`,
        `annotated_created: ${imageResult.annotatedCount}`,
        `skipped: ${imageResult.skipped.length}`,
      ];

      zip.file("README.txt", readmeLines.join("\n"));

      await saveZip(
        zip,
        mode === "annotated"
          ? `stomata_export_annotated_v2_${ts}.zip`
          : `stomata_export_original_v2_${ts}.zip`,
        progressKey
      );
    },
    [selectedData, hydrateDocs, addImagesToZip, saveZip, t]
  );

  const downloadFullExportZip = useCallback(
    async () => {
      const docs = selectedData;
      if (!docs.length) return;

      const ts = getCurrentDateTime();
      const zip = new JSZip();
      const progressKey = "zip-progress";

      const hydrated = await hydrateDocs(docs, {
        includeResults: true,
        progressLabel: t("download.ui.fetchingAll"),
        progressKey,
      });

      zip.file("csv/summary.csv", withBom(buildSummaryCsvContent(hydrated)));
      zip.file("csv/stomata.csv", withBom(buildStomataCsvContent(hydrated)));
      zip.file("csv/environment.csv", withBom(buildEnvironmentCsvContent(hydrated)));

      const imageResult = await addImagesToZip(zip, hydrated, {
        includeOriginal: true,
        includeAnnotated: true,
        originalDir: "images/original",
        annotatedDir: "images/annotated",
        progressKey,
      });

      zip.file(
        "csv/skipped_images.csv",
        withBom(buildSkippedImagesCsvContent(imageResult.skipped))
      );

      const readmeLines = [
        "StomataScope Full Export",
        `created_at: ${ts}`,
        `selected_images: ${docs.length}`,
        "",
        "Folders:",
        "- images/original/  : original images",
        "- images/annotated/ : annotated images",
        "- csv/              : all CSV files",
        "",
        "CSV files included:",
        "- summary.csv       : one row per image",
        "- stomata.csv       : one row per stoma",
        "- environment.csv   : environment metadata",
        "- skipped_images.csv: skipped image log",
        "",
        `original_created: ${imageResult.originalCount}`,
        `annotated_created: ${imageResult.annotatedCount}`,
        `skipped: ${imageResult.skipped.length}`,
      ];

      zip.file("README.txt", readmeLines.join("\n"));

      await saveZip(zip, `stomata_full_export_${ts}.zip`, progressKey);
    },
    [selectedData, hydrateDocs, addImagesToZip, saveZip, t]
  );

  const handleDownload = useCallback(async () => {
    if (!selectedData.length) {
      message.warning(t("download.ui.selectRequired"));
      return;
    }

    try {
      /**
       * 新規CSV
       */
      if (selectedType === "summary_csv") {
        await downloadSummaryCsv(selectedData);
        return;
      }

      if (selectedType === "stomata_csv") {
        await downloadStomataCsv(selectedData);
        return;
      }

      if (selectedType === "environment_csv") {
        await downloadEnvironmentCsv(selectedData);
        return;
      }

      /**
       * 既存画像ZIP
       */
      if (selectedType === "image_original") {
        await downloadImagesZipLegacy("original");
        return;
      }

      if (selectedType === "image_annotated") {
        await downloadImagesZipLegacy("annotated");
        return;
      }

      /**
       * 新規画像ZIP
       */
      if (selectedType === "image_original_v2") {
        await downloadImagesZipV2("original");
        return;
      }

      if (selectedType === "image_annotated_v2") {
        await downloadImagesZipV2("annotated");
        return;
      }

      /**
       * 新規フルエクスポート
       */
      if (selectedType === "full_export_zip") {
        await downloadFullExportZip();
        return;
      }

      /**
       * 既存CSV
       * aperture / axisLong / axisShort / area
       */
      const csv = await buildLegacyCsv(selectedData, selectedResultFields);
      const tag = selectedResultFields.join("_") || "all";

      downloadCsvFile(csv, tag);

      message.success({
        content: t("download.ui.csvDownloaded"),
        key: "download-progress",
        duration: 2,
      });
    } catch (e) {
      console.error(e);
      message.error(t("download.ui.downloadFailed"));
    }
  }, [
    selectedData,
    selectedType,
    selectedResultFields,
    buildLegacyCsv,
    downloadCsvFile,
    downloadSummaryCsv,
    downloadStomataCsv,
    downloadEnvironmentCsv,
    downloadImagesZipLegacy,
    downloadImagesZipV2,
    downloadFullExportZip,
  , t]);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      preserveSelectedRowKeys: true,
      onChange: handleSelectChange,
      selections: [
        {
          key: "select-filtered-loaded",
          text: t("download.ui.selectLoaded"),
          onSelect: () => {
            setSelectedRowKeys(filteredPosts.map((p) => p.key));
          },
        },
        {
          key: "clear-selection",
          text: t("download.ui.clearSelection"),
          onSelect: () => {
            setSelectedRowKeys([]);
          },
        },
      ],
    }),
    [selectedRowKeys, handleSelectChange, filteredPosts, language, t]
  );

  return (
    <ConfigProvider locale={language?.startsWith("ja") ? jaJP : enUS}>
    <div style={{ padding: 24, background: colorBgContainer, height: "100%" }}>
      {loading ? (
        <div className="spin">
          <StomataLoader />
        </div>
      ) : !user ? (
        <Navigate to={"/"} />
      ) : (
        <>
          <Space style={{ marginBottom: 16 }} wrap>

            {!isAdmin ? (
              null
            ) :
              <>
                <Input.Search
                  placeholder={t("download.ui.targetEmail")}
                  value={targetEmailInput}
                  onChange={(e) => setTargetEmailInput(e.target.value)}
                  onSearch={handleTargetEmailSearch}
                  enterButton={t("download.ui.load")}
                  allowClear
                  disabled={!isAdmin}
                  style={{ width: 360 }}
                />

                <Button onClick={handleLoadOwnData} disabled={!user?.email}>
                  {t("download.ui.ownData")}
                </Button>

                <span style={{ color: "#555" }}>
                  {t("download.ui.target")}: {activeTargetEmail || user?.email || "-"}
                </span>
              </>
            }

            <RangePicker
              value={dateRange}
              onChange={handleDateChange}
              allowClear
            />

            <Button onClick={handleResetDate} disabled={!isDateFiltered}>
              {t("download.ui.reset")}
            </Button>

            <Button
              onClick={loadMorePosts}
              loading={loadingMore}
              disabled={!hasMore || loadingMore || loadingAll}
            >
              {t("download.ui.loadMore", { count: LOAD_STEP })}
            </Button>

            <Button
              onClick={loadAllRows}
              loading={loadingAll}
              disabled={loadingAll || loadedAll}
            >
              {t("download.ui.loadAll")}
            </Button>

            <span style={{ color: "#888" }}>
              {t("download.ui.recordCounts", { shown: filteredPosts.length, loaded: posts.length })}
              {loadedAll ? t("download.ui.allSuffix") : ""}
            </span>

            {isDateFiltered && !loadedAll ? (
              <span style={{ color: "#c27c00" }}>
                {t("download.ui.filterNote")}
              </span>
            ) : null}
          </Space>

          <Select
            value={selectedType}
            style={{ width: 420, marginBottom: 16 }}
            onChange={(value) => setSelectedType(value)}
            options={[
              /**
               * 既存: pixel
               */
              {
                label: <span>{t("download.group.pixel")}</span>,
                title: t("download.group.pixel"),
                options: [
                  { label: <span>{t("download.ui.field_axisLong")}</span>, value: "axisLong" },
                  { label: <span>{t("download.ui.field_axisShort")}</span>, value: "axisShort" },
                ],
              },

              /**
               * 既存: µm
               */
              {
                label: <span>µm</span>,
                title: "µm",
                options: [{ label: <span>{t("download.ui.field_aperture")}</span>, value: "aperture" }],
              },

              /**
               * 既存: µm²
               */
              {
                label: <span>µm²</span>,
                title: "µm²",
                options: [{ label: <span>{t("download.ui.field_area")}</span>, value: "area" }],
              },

              /**
               * 既存: image
               */
              {
                label: <span>{t("download.group.image")}</span>,
                title: t("download.group.image"),
                options: [
                  {
                    label: <span>{t("download.jpegAnnotated")}</span>,
                    value: "image_annotated",
                  },
                  {
                    label: <span>{t("download.jpegOriginal")}</span>,
                    value: "image_original",
                  },
                ],
              },

              /**
               * 新規追加:
               * 既存valueは触らず、下に追加
               */
              {
                label: <span>{t("download.ui.export")}</span>,
                title: t("download.ui.export"),
                options: [
                  {
                    label: <span>{t("download.ui.summaryCsv")}</span>,
                    value: "summary_csv",
                  },
                  {
                    label: <span>{t("download.ui.stomataCsv")}</span>,
                    value: "stomata_csv",
                  },
                  {
                    label: <span>{t("download.ui.environmentCsv")}</span>,
                    value: "environment_csv",
                  },
                  {
                    label: <span>{t("download.ui.originalZip")}</span>,
                    value: "image_original_v2",
                  },
                  {
                    label: <span>{t("download.ui.annotatedZip")}</span>,
                    value: "image_annotated_v2",
                  },
                  {
                    label: <span>{t("download.ui.fullZip")}</span>,
                    value: "full_export_zip",
                  },
                ],
              },
            ]}
          />

          <Button
            size="large"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            disabled={!selectedRowKeys.length}
            style={{ marginLeft: 8, marginBottom: 16 }}
          >
            {t("download.button")}
          </Button>

          <Table
            locale={tableLocale}
            rowKey="key"
            size="small"
            columns={columns}
            dataSource={filteredPosts}
            rowSelection={rowSelection}
            pagination={{
              defaultPageSize: TABLE_PAGE_SIZE,
              showSizeChanger: true,
              pageSizeOptions: ["50", "100", "200", "500"],
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
            }}
            scroll={{ y: "70vh" }}
            expandable={{
              onExpand: handleExpand,
              expandedRowRender: (record) => (
                <ExpandedResults
                  results={resultsByKey[record.key] || []}
                  loading={Boolean(expandedLoadingKeys[record.key])}
                  fields={selectedResultFields}
                />
              ),
            }}
          />
        </>
      )}
    </div>
    </ConfigProvider>
  );
}

/**
 * 列定義
 * 既存UIをなるべく維持
 */
function getColumns(t, language) {
  return [
    {
      title: t("download.cols.photopath"),
      dataIndex: "photopath",
      key: "photopath",
      width: "20%",
      sorter: (a, b) => (a.photopath || "").localeCompare(b.photopath || ""),
      sortDirections: ["ascend", "descend", "ascend"],
    },
    {
      title: t("download.cols.cultivar"),
      dataIndex: "cultiverName",
      key: "cultiverName",
      width: "20%",
      sorter: (a, b) => (a.cultiverName || "").localeCompare(b.cultiverName || ""),
      sortDirections: ["ascend", "descend", "ascend"],
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t("download.filters.cultivar.placeholder")}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            {t("common.search")}
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            {t("common.reset")}
          </Button>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record.cultiverName
          ? record.cultiverName.toLowerCase().includes(String(value).toLowerCase())
          : false,
    },
    {
      title: t("download.cols.date"),
      dataIndex: "date",
      key: "date",
      width: "12%",
      sorter: (a, b) => (a.timestampMs ?? 0) - (b.timestampMs ?? 0),
    },
    {
      title: t("download.cols.aperture"),
      dataIndex: "aperture",
      key: "aperture",
      width: "8%",
      sorter: (a, b) => (a.aperture ?? 0) - (b.aperture ?? 0),
      sortDirections: ["ascend", "descend", "ascend"],
      render: (value) => (typeof value === "number" ? value.toFixed(2) : ""),
    },
    {
      title: t("download.cols.count"),
      dataIndex: "size",
      key: "size",
      width: "8%",
      sorter: (a, b) => (Number(a.size) || 0) - (Number(b.size) || 0),
      sortDirections: ["ascend", "descend", "ascend"],
    },
    {
      title: t("download.cols.location"),
      dataIndex: "location",
      key: "location",
      width: "12%",
      sorter: (a, b) => (a.location || "").localeCompare(b.location || ""),
      sortDirections: ["ascend", "descend", "ascend"],
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t("download.filters.location.placeholder")}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            {t("common.search")}
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            {t("common.reset")}
          </Button>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        (record.location || "").toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: t("download.cols.importance"),
      dataIndex: "important",
      key: "important",
      width: "8%",
      sorter: (a, b) => (a.important || "").localeCompare(b.important || ""),
      sortDirections: ["ascend", "descend", "ascend"],
      filters: [
        { text: t("output_form.none"), value: "" },
        { text: "★", value: "★" },
        { text: "★★", value: "★★" },
        { text: "★★★", value: "★★★" },
      ],
      onFilter: (value, record) => (record.important ?? "") === value,
    },
    {
      title: t("download.ui.updated"),
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: "14%",
      sorter: (a, b) => {
        const ta = a.updatedAt?.toDate
          ? a.updatedAt.toDate().getTime()
          : 0;

        const tb = b.updatedAt?.toDate
          ? b.updatedAt.toDate().getTime()
          : 0;

        return ta - tb;
      },
      render: (value) => {
        if (!value?.toDate) return "";

        return value.toDate().toLocaleString(language?.startsWith("ja") ? "ja-JP" : "en-US", {
          year: "2-digit",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },
    {
      title: t("download.cols.freeText"),
      dataIndex: "freeText",
      key: "freeText",
      width: "20%",
      sorter: (a, b) => (a.freeText || "").localeCompare(b.freeText || ""),
      sortDirections: ["ascend", "descend", "ascend"],
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t("download.filters.freeText.placeholder")}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            {t("common.search")}
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            {t("common.reset")}
          </Button>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        (record.freeText || "").toLowerCase().includes(String(value).toLowerCase()),
    },
  ];
}

export default Download;